import React, { ReactElement } from 'react';

import type { BulkEmailDTO } from '../../services/apiClient';
import { Send } from 'lucide-react';

import Table from '../../components/common/Table';

interface BulkEmailTableProps {
  emails: BulkEmailDTO[];
  handleEdit: (email: BulkEmailDTO) => void;
  handleDelete: (email: BulkEmailDTO) => void;
  handleProcessSend: (email: BulkEmailDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
}

const BulkEmailTable: React.FC<BulkEmailTableProps> = ({
  emails,
  handleEdit,
  handleDelete,
  handleProcessSend,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
}) => (
  <Table
    columns={[
      { label: 'Subject', key: 'subject' },
      { label: 'Send To', key: 'communicationListName' },
      {
        label: 'Status',
        key: 'bulkEmailStatusName',
        render: (row: BulkEmailDTO): ReactElement => {
          switch (row.bulkEmailStatusName) {
            case 'Pending':
              return (
                <span className="px-2 py-1 text-xs font-bold text-yellow-800 bg-yellow-200 rounded-full">
                  Pending
                </span>
              );
            case 'Processing':
              return (
                <span className="px-2 py-1 text-xs font-bold text-blue-800 bg-blue-200 rounded-full">
                  Processing
                </span>
              );
            case 'Complete':
              return (
                <span className="px-2 py-1 text-xs font-bold text-green-800 bg-green-200 rounded-full">
                  Complete
                </span>
              );
            default:
              return (
                <span className="px-2 py-1 text-xs font-bold text-gray-800 bg-gray-200 rounded-full">
                  Unknown
                </span>
              );
          }
        },
      },
      {
        label: 'Content Preview',
        key: 'content',
        render: (row: BulkEmailDTO) =>
          (row.content ?? '').length > 50 ? (
            <span title={row.content}>
              {(row.content ?? '').slice(0, 50)}...
            </span>
          ) : (
            <span>{row.content}</span>
          ),
      },
      {
        label: 'Total Count',
        key: 'totalCount',
        render: (row: BulkEmailDTO) =>
          row.bulkEmailStatusName === 'Pending' ? (
            <span>Pending...</span>
          ) : (
            <span>{row.totalCount || 0}</span>
          ),
      },
      {
        label: 'Total Processed',
        key: 'totalCountProcessed',
        render: (row: BulkEmailDTO) =>
          row.bulkEmailStatusName === 'Pending' ? (
            <span>Pending...</span>
          ) : (
            <span>{row.totalCountProcessed || 0}</span>
          ),
      },
    ]}
    data={emails}
    actions={{
      onEdit: handleEdit,
      onDelete: handleDelete,
      extra: [
        {
          label: 'Process Bulk Send',
          icon: <Send />,
          confirmMessage: 'Are you sure you want to process this bulk email?',
          onClick: handleProcessSend,
        },
      ],
    }}
    currentPage={currentPage}
    totalPages={totalPages}
    onPageChange={setCurrentPage}
    pageSize={pageSize}
    onPageSizeChange={setPageSize}
    isLoading={isLoading}
  />
);

export default BulkEmailTable;
